import { Button } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import classNames from 'classnames';
import React, { useState } from 'react';

import { DepartmentEnum, PlexMemberRole, useGetLoggedInUserQuery } from '../../../../graphql/gen/types';
import { colorPalette } from '../../../../theme';
import { AccessControlEnumData, DepartmentEnumData } from '../../../../utils/enumData';
import { PlexSelect } from '../../../shared/primitives/PlexSelect';
import { PlexTextField } from '../../../shared/primitives/PlexTextField';
import { PlexName } from '../../common/PlexName';
import { EditableAvatar } from '../EditableAvatar';
import { Heading } from '../Heading';
import { SettingsContent } from '../SettingsContent';
import { useAccountStyles } from './styles';
import { SettingsPageProps } from './types';

const ALL = 'all';

const roleDisplay: Record<string, string> = {
  [ALL]: 'All',
  [PlexMemberRole.Owner]: 'Owner',
  [PlexMemberRole.Manager]: 'Lead',
  [PlexMemberRole.Writer]: 'Team member',
};

export const Profile = ({
  savedSettings,
  unsavedSettings,
  loadingMutation,
  onChange,
  onSave,
  onCancel,
  updateDisabled,
}: SettingsPageProps): React.ReactElement => {
  const accountClasses = useAccountStyles();
  const [selectedPlexRelation, setSelectedPlexRelation] = useState<string>('all');
  const loggedInUser = useGetLoggedInUserQuery({ variables: {} });
  const classes = useStyles();
  return (
    <SettingsContent
      title={'Profile'}
      onSave={onSave}
      onCancel={onCancel}
      updateDisabled={updateDisabled}
      loadingMutation={loadingMutation}
    >
      <div className={classes.avatarContainer}>
        <EditableAvatar
          alt={'avatar'}
          picture={unsavedSettings.picture || savedSettings.picture}
          loading={loadingMutation}
          onUploadComplete={(file: string): void => onChange({ picture: file })}
        />
      </div>
      <PlexTextField
        id={'profile-name'}
        label={'Name'}
        className={accountClasses.borderRight}
        value={unsavedSettings.name || savedSettings.name}
        handleChange={(value): void => onChange({ name: value })}
      />
      <PlexTextField
        id={'profile-title'}
        label={'Title'}
        className={accountClasses.halfWidth}
        value={unsavedSettings.jobTitle || savedSettings.jobTitle}
        handleChange={(value): void => onChange({ jobTitle: value })}
      />
      <PlexSelect
        label={'Department'}
        options={DepartmentEnumData}
        value={unsavedSettings.jobDepartment || savedSettings.jobDepartment}
        handleChange={(value): void => onChange({ jobDepartment: value as DepartmentEnum })}
        className={accountClasses.borderRight}
      />
      <PlexTextField
        id={'profile-domain'}
        label={'Domain'}
        className={accountClasses.borderRight}
        disabled={true}
        value={savedSettings.domain?.url}
      />
      <PlexTextField
        id={'profile-email'}
        label={'Email address'}
        className={accountClasses.borderRight}
        disabled
        value={savedSettings.email}
      />
      <PlexSelect
        label={'Access Control'}
        options={AccessControlEnumData}
        value={savedSettings.accessControl}
        disabled
        className={accountClasses.halfWidth}
      />

      <Heading text={'My Plexes'} />

      <div className={classes.row}>
        {Object.keys(roleDisplay).map((role) => {
          const isSelected = selectedPlexRelation === role;
          return (
            <div key={role} className={classes.relationContainer}>
              <Button
                className={isSelected ? classes.relationSelected : undefined}
                onClick={(): void => setSelectedPlexRelation(role)}
              >
                {roleDisplay[role]}
              </Button>
              {isSelected && <div className={classes.selectedItemHighlight} />}
            </div>
          );
        })}
      </div>

      <div className={classNames(classes.row, classes.myPlexesRow)}>
        {loggedInUser.data?.currentUser?.plexes
          ?.map((plex, i) => ({ plex, i }))
          .filter(({ plex }) => selectedPlexRelation === ALL || plex.currentUserRelation.role === selectedPlexRelation)
          .map(({ plex }) => {
            return (
              <div className={classes.chip} key={plex.name}>
                <PlexName name={plex.name} color={plex.color} />
              </div>
            );
          })}
      </div>
    </SettingsContent>
  );
};

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    avatarContainer: {
      width: '100%',
    },
    row: {
      paddingLeft: 48,
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      boxSizing: 'border-box',
      borderBottom: `1px solid ${colorPalette.gray._300}`,
      flexWrap: 'wrap',
    },
    relationContainer: {
      position: 'relative',
      padding: '16px 0 19px 0',
      '&:first-child': {
        marginLeft: -22,
      },
    },
    relationSelected: {
      color: colorPalette.primaryColorBlue._500,
    },
    selectedItemHighlight: {
      position: 'absolute',
      bottom: 0,
      left: '50%',
      marginLeft: -10,
      borderTopRightRadius: 5,
      borderTopLeftRadius: 5,
      width: 20,
      height: 5,
      backgroundColor: colorPalette.primaryColorBlue._500,
    },
    myPlexesRow: {
      paddingBottom: 28,
      paddingRight: 24,
      marginLeft: -15,
    },
    chip: {
      marginTop: 28,
      marginLeft: 5,
    },
  })
);
